import Overlay from 'components/Basic/Overlay/Overlay';
import { Portal } from 'components/Basic/Portal/Portal';
import Contact from 'components/Layout/Header/Contact/Contact';
import { DeliveryAddressSelector } from 'components/Layout/Header/DeliveryAddress/DeliveryAddressSelector';
import Cart from 'components/Layout/Header/HeaderBottom/Cart/Cart';
import DesktopMenu from 'components/Layout/Header/HeaderBottom/DesktopMenu/DesktopMenu';
import HamburgerButton from 'components/Layout/Header/HeaderBottom/HamburgerButton/HamburgerButton';
import { MenuIconic } from 'components/Layout/Header/HeaderBottom/MenuIconic/MenuIconic';
import { MobileMenuProps } from 'components/Layout/Header/HeaderBottom/MobileMenu/MobileMenu';
import Search from 'components/Layout/Header/HeaderBottom/Search/Search';
import { QuickOrderButton } from 'components/Layout/Header/QuickOrder/QuickOrderButton';
import { MENU_HOVER_DELAY, MOBILE_MENU_ANIMATION } from 'config/constants';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { isB2bDomain, isWebDomain } from 'helpers/isDomainLocale';
import useTimeout from 'hooks/helpers/UseTimeout';
import { useEffectOnce } from 'hooks/ui/useEffectOnce';
import { useMediaMin } from 'hooks/ui/useMediaMin';
import { useMountAnimation } from 'hooks/ui/useMountAnimation';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { twJoin } from 'tailwind-merge';
import { CurrentCustomerType } from 'types/customer';

const MobileMenu = dynamic<MobileMenuProps>(
    import('./MobileMenu/MobileMenu').then((component) => component.MobileMenu),
);

type HeaderBottomProps = {
    currentCustomerUser?: CurrentCustomerType | null;
};

export const HeaderBottom: FC<HeaderBottomProps> = ({ currentCustomerUser = null }) => {
    const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false);
    const [isMobileMenuOpen, isMobileMenuInStableAnimationState, setIsMobileMenuOpen] = useMountAnimation(
        MOBILE_MENU_ANIMATION,
        false,
    );
    const [isDesktopMenuButtonHovered, setIsDesktopMenuButtonHovered] = useState(false);
    const [isDesktopMenuHovered, setIsDesktopMenuHovered] = useState(false);
    const [isDesktopMenuButtonFocused, setIsDesktopMenuButtonFocused] = useState(false);
    const [isDesktopMenuFocused, setIsDesktopMenuFocused] = useState(false);
    const { events } = useRouter();
    const isDesktop = useMediaMin('vl');
    const domainConfig = useDomainConfig();

    // prevent the menu from being accidentally opened and closed
    useTimeout(
        () => setIsDesktopMenuOpen(true),
        !isDesktopMenuOpen && (isDesktopMenuButtonHovered || isDesktopMenuHovered) ? MENU_HOVER_DELAY : null,
    );

    useTimeout(
        () => setIsDesktopMenuOpen(false),
        isDesktopMenuOpen &&
            !isDesktopMenuButtonHovered &&
            !isDesktopMenuHovered &&
            !isDesktopMenuButtonFocused &&
            !isDesktopMenuFocused
            ? MENU_HOVER_DELAY
            : null,
    );

    useEffectOnce(() => {
        const closeMenuOnRouteComplete = () => {
            setIsMobileMenuOpen(false);
            setIsDesktopMenuOpen(false);
            setIsDesktopMenuButtonHovered(false);
            setIsDesktopMenuHovered(false);
            setIsDesktopMenuButtonFocused(false);
            setIsDesktopMenuFocused(false);
        };

        const handleEscapeKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && (isDesktopMenuOpen || isMobileMenuOpen)) {
                closeMenuOnRouteComplete();
            }
        };

        events.on('routeChangeComplete', closeMenuOnRouteComplete);
        document.addEventListener('keydown', handleEscapeKey);

        return () => {
            events.off('routeChangeComplete', closeMenuOnRouteComplete);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    });

    return (
        <>
            <div className="flex w-[135px] flex-1 justify-end bg-primary vl:hidden">
                <MenuIconic />
                {!isWebDomain(domainConfig) && <Cart isDesktop={false} />}
            </div>
            <div className="flex items-center justify-start vl:h-[80px] vl:gap-x-4 notVl:bg-primary">
                <HamburgerButton
                    isMenuOpened={isDesktopMenuOpen}
                    showCloseIcon={isDesktop === false && isMobileMenuOpen}
                    onBlur={isDesktop ? () => setIsDesktopMenuButtonFocused(false) : undefined}
                    onFocus={isDesktop ? () => setIsDesktopMenuButtonFocused(true) : undefined}
                    onMouseEnter={isDesktop ? () => setIsDesktopMenuButtonHovered(true) : undefined}
                    onMouseLeave={isDesktop ? () => setIsDesktopMenuButtonHovered(false) : undefined}
                    onClickCallback={(event) => {
                        if (isDesktop) {
                            event.preventDefault();
                            setIsDesktopMenuOpen((prev) => !prev);
                        } else {
                            setIsMobileMenuOpen(!isMobileMenuOpen);
                        }
                    }}
                />
                {isB2bDomain(domainConfig) ? <QuickOrderButton /> : <Contact desktopOnly />}
            </div>
            {isMobileMenuOpen && (
                <MobileMenu
                    setIsMobileMenuOpen={setIsMobileMenuOpen}
                    shouldStartAnimation={isMobileMenuInStableAnimationState}
                />
            )}

            {currentCustomerUser && isB2bDomain(domainConfig) && !isDesktop && (
                <div className="block w-full px-1 pb-1 vl:hidden">
                    <DeliveryAddressSelector currentCustomerUser={currentCustomerUser} />
                </div>
            )}

            <div
                className={twJoin(
                    'flex items-center vl:h-[80px] vl:flex-1 vl:gap-x-2 vl:px-3 xl:px-5 notVl:w-full',
                    isMobileMenuOpen && 'pointer-events-none opacity-30',
                )}
            >
                <Search />
            </div>
            <div
                className={twJoin(
                    'flex items-center justify-end gap-x-0 notVl:hidden',
                    isWebDomain(domainConfig) ? 'min-w-[150px]' : 'min-w-[280px]',
                )}
            >
                <MenuIconic />
                {!isWebDomain(domainConfig) && <Cart isDesktop />}
            </div>
            {isDesktop && (
                <>
                    {isDesktopMenuOpen && (
                        <DesktopMenu
                            isOpen={isDesktopMenuOpen}
                            onFocus={() => setIsDesktopMenuFocused(true)}
                            onMouseEnter={() => setIsDesktopMenuHovered(true)}
                            onMouseLeave={() => setIsDesktopMenuHovered(false)}
                            onBlur={(e) => {
                                // Check if focus is moving outside the menu container
                                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                                    setIsDesktopMenuFocused(false);
                                }
                            }}
                        />
                    )}
                    <Overlay className="z-overlay" isActive={isDesktopMenuOpen} withWrapper={false} />
                </>
            )}
            {!isDesktop && isMobileMenuOpen && (
                <Portal>
                    <Overlay isActive className="z-overlay" onClick={() => setIsMobileMenuOpen(false)} />
                </Portal>
            )}
        </>
    );
};
